/**
 * What the AI would know about the company on a test call, in the dialog.
 *
 * The brain's sales arguments first, because they are the reason the AI gives
 * for calling; then the call script's hooks, questions and answers to
 * objections; then the background it must never say out loud (the verdict) and
 * the offer. A company the brain never looked at says so, with the way to fix
 * it: an AI with nothing to say about a company is the most common reason a
 * test call sounds empty.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Brain } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { analysisAge, brainPath, knowsSummary, type AiOutboundTestCallPreview } from "../../../lib/aiTestCallPreview";
import { StatusPill } from "../../ui";

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <section className="space-y-1.5">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title} <span className="font-normal">({count})</span>
      </h4>
      <ul className="space-y-1.5 text-sm">{children}</ul>
    </section>
  );
}

export function AiKnows({
  knows,
  domain,
  onNavigate,
}: {
  knows: AiOutboundTestCallPreview["knows"];
  domain: string;
  /** Called when a link leaves the dialog, so the dialog closes over the page it opened. */
  onNavigate?: () => void;
}) {
  const t = useT();
  const summary = knowsSummary(knows);
  const age = analysisAge(knows.analysedDaysAgo);

  if (summary.empty) {
    return (
      <div className="space-y-2 rounded-lg border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
        <p>{t("aiOutbound.knows.empty")}</p>
        {domain ? (
          <Link to={brainPath(domain)} className="inline-flex items-center gap-1.5 text-primary hover:underline" onClick={onNavigate}>
            <Brain className="size-3.5" />
            {t("aiOutbound.knows.openBrain")}
          </Link>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>{t(`aiOutbound.knows.age.${age.key}`, { n: age.n })}</span>
        {summary.stale ? (
          <StatusPill tone="wt">
            <AlertTriangle className="size-3" />
            {t("aiOutbound.knows.stale")}
          </StatusPill>
        ) : null}
        {domain ? (
          <Link to={brainPath(domain)} className="inline-flex items-center gap-1 text-primary hover:underline" onClick={onNavigate}>
            <Brain className="size-3" />
            {t("aiOutbound.knows.openBrain")}
          </Link>
        ) : null}
      </div>

      <Section title={t("aiOutbound.knows.arguments")} count={summary.arguments}>
        {knows.arguments.map((a, i) => (
          <li key={`a${i}`} className="rounded-md bg-muted/40 px-2.5 py-1.5">
            <span className="text-foreground">{a.claim}</span>
            {a.impact ? <span className="block text-xs text-muted-foreground">{a.impact}</span> : null}
          </li>
        ))}
      </Section>

      <Section title={t("aiOutbound.knows.hooks")} count={summary.hooks}>
        {knows.hooks.map((h, i) => (
          <li key={`h${i}`} className="rounded-md bg-muted/40 px-2.5 py-1.5">
            <span className="text-foreground">{h.text}</span>
            {h.fix ? <span className="block text-xs text-muted-foreground">{h.fix}</span> : null}
          </li>
        ))}
      </Section>

      <Section title={t("aiOutbound.knows.questions")} count={summary.questions}>
        {knows.questions.map((q, i) => (
          <li key={`q${i}`} className="px-2.5">
            {q}
          </li>
        ))}
      </Section>

      <Section title={t("aiOutbound.knows.objections")} count={summary.objections}>
        {knows.objections.map((o, i) => (
          <li key={`o${i}`} className="rounded-md bg-muted/40 px-2.5 py-1.5">
            <span className="text-xs text-muted-foreground">{t("aiOutbound.knows.cue", { cue: o.cue })}</span>
            <span className="block text-foreground">{o.answer}</span>
          </li>
        ))}
      </Section>

      {knows.fitDecision || knows.fitReason ? (
        <p className="text-xs text-muted-foreground">
          {t("aiOutbound.knows.background", {
            verdict: knows.fitDecision ? t(`aiOutbound.knows.fit.${knows.fitDecision}`) : "-",
            reason: knows.fitReason ?? "-",
          })}
        </p>
      ) : null}
      {knows.pitch ? (
        <p className="text-xs text-muted-foreground">{t("aiOutbound.knows.pitch", { pitch: knows.pitch })}</p>
      ) : null}
    </div>
  );
}
